import React from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import InputField from "components/FormFields/InputField";
import Button from "components/Buttons/Button";
import Render from "components/Common/Render";

interface IForgotPasswordFormProps {
  onSubmit: (identifier: string) => void;
  isLoading: boolean;
  onBackToLogin?: () => void;
}

const validationSchema = Yup.object({
  email: Yup.string()
    .email("Please enter a valid email address")
    .required("Email address is required"),
});

const ForgotPasswordForm: React.FC<IForgotPasswordFormProps> = ({
  onSubmit,
  isLoading,
  onBackToLogin,
}) => {
  const formik = useFormik({
    initialValues: {
      email: "",
    },
    validationSchema,
    validateOnMount: false,
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: (values) => {
      onSubmit(values.email.trim());
    },
  });

  return (
    <div className="flex flex-col w-full">
      <h1 className="font-poppins font-semibold text-[28px] md:text-[32px] leading-[100%] tracking-[0] text-gray-900 mb-2">
        Forgot your password?
      </h1>
      <p className="font-poppins pt-4 font-normal text-[14px] md:text-[16px] leading-[100%] tracking-[0] text-gray-600 mb-8">
        Don't worry, happens to all of us. Enter your email below to recover
        your password
      </p>

      {/* Form */}
      <form onSubmit={formik.handleSubmit} className="space-y-6">
        <InputField
          label="Email Address"
          type="email"
          value={formik.values.email}
          placeholder="Enter email address"
          onChange={(e) => formik.setFieldValue("email", e.target.value)}
          error={formik.touched.email && formik.errors.email}
          customClass="h-12 px-4"
        />

        {/* Buttons Container */}
        <div className="flex flex-col md:flex-row gap-2 pt-3">
          <Button
            type="submit"
            label={isLoading ? "Loading..." : "Submit"}
            disabled={!formik.isValid || isLoading || !formik.dirty}
            loading={isLoading}
            variant="primary"
            customClass="!mb-0 !me-0 w-full md:w-[240px] order-1 md:order-2"
          />

          <Render when={!!onBackToLogin}>
            <Button
              type="button"
              label="Back to login"
              onClick={onBackToLogin}
              variant="secondary"
              customClass="!mb-0 !me-0 w-full md:w-[240px] order-2 md:order-1"
            />
          </Render>
        </div>
      </form>
    </div>
  );
};

export default ForgotPasswordForm;
